import { memo, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export type SettingsSectionCardTone =
  | 'indigo'
  | 'violet'
  | 'amber'
  | 'sky'
  | 'emerald'
  | 'slate';

export interface SettingsSectionCardProps {
  readonly icon: LucideIcon;
  readonly tone?: SettingsSectionCardTone;
  readonly title: ReactNode;
  readonly lead?: string;
  /** Optional control beside the title (e.g. muted MetricExplainButton). */
  readonly titleEnd?: ReactNode;
  /** Optional strip above the section title (e.g. account identity). */
  readonly prelude?: ReactNode;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  readonly className?: string;
  readonly 'data-testid'?: string;
}

export const SettingsSectionCard = memo(
  ({
    icon: Icon,
    tone = 'slate',
    title,
    lead,
    titleEnd,
    prelude,
    children,
    footer,
    className = '',
    'data-testid': dataTestId,
  }: SettingsSectionCardProps): JSX.Element => {
    const rootClass = ['asp-tone-card', `asp-tone-card--${tone}`, className]
      .filter((part) => part.length > 0)
      .join(' ');

    return (
      <article className={rootClass} data-testid={dataTestId}>
        {prelude != null ? (
          <div className="asp-tone-card__prelude min-w-0" data-testid="settings-section-card-prelude">
            {prelude}
          </div>
        ) : null}
        <header className="asp-tone-card-head">
          <span className="asp-tone-card-icon" aria-hidden="true">
            <Icon className="h-5 w-5 stroke-[1.75]" />
          </span>
          <div>
            <h3 className="asp-tone-card-title">
              <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
                <span>{title}</span>
                {titleEnd != null ? titleEnd : null}
              </span>
            </h3>
            {lead != null && lead !== '' ? <p className="asp-tone-card-lead">{lead}</p> : null}
          </div>
        </header>
        {children != null ? (
          <div className="asp-tone-card__body flex min-w-0 flex-col gap-3">{children}</div>
        ) : null}
        {footer != null ? footer : null}
      </article>
    );
  },
);

SettingsSectionCard.displayName = 'SettingsSectionCard';
